import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const STORAGE_KEY = "ftc-push-prompt-state-v1";

interface PromptState { dismissed?: number; granted?: boolean }

function getState(): PromptState {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
}
function setState(s: PromptState) { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); }

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - base64.length % 4) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}

export function PushPermissionPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (Notification.permission === "granted" || Notification.permission === "denied") return;
    const s = getState();
    if (s.granted) return;
    if (s.dismissed && Date.now() - s.dismissed < 30 * 24 * 60 * 60 * 1000) return; // 30 day cooldown
    setShow(true);
  }, []);

  async function enable() {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setShow(false); setState({ dismissed: Date.now() }); return; }
      const keyResp = await fetch("/api/push/vapid-public-key");
      const { publicKey } = await keyResp.json();
      if (!publicKey) { setShow(false); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await fetch("/api/push/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(sub.toJSON()),
      });
      setState({ granted: true });
      setShow(false);
    } catch (e) {
      console.error("[push] enable failed", e);
      setShow(false);
    }
  }

  function dismiss() { setState({ dismissed: Date.now() }); setShow(false); }

  if (!show) return null;
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-card border border-border shadow-lg rounded-lg px-4 py-3 flex items-center gap-3 max-w-xl">
      <span className="text-sm">Get desktop notifications when reminders fire?</span>
      <Button size="sm" onClick={enable}>Enable</Button>
      <Button size="sm" variant="ghost" onClick={dismiss}>Not now</Button>
      <button onClick={dismiss} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
    </div>
  );
}
